/**
 * Twilio Voice Service for VRS calls.
 *
 * This registers the current interpreter as a Twilio Client so the PSTN leg
 * has a stable browser/mobile identity to dial.
 */
import { vcoSpeakingResponseSchema, voiceAttemptCancellationResponseSchema } from '../../../../contracts/voice';
import { timeoutPromise } from '../../base/util/timeoutPromise';
import { reportMobileError } from '../../mobile/navigation/errorReporting';
import { emitMobileTelemetryEvent } from '../../mobile/navigation/telemetry';
import { apiClient, unwrapApiResponse } from '../../shared/api-client';
import { getPersistentItem, getPersistentJson } from '../../vrs-auth/storage';
import { clearBrowserVoiceAttempt, getBrowserVoiceActorId, loadBrowserVoiceAttempt, ownsBrowserVoiceAttempt, reserveBrowserVoiceAttempt } from './browserVoiceAttempt';
const phoneProviderStatuses = ['queued', 'initiated', 'holding', 'answering', 'ringing',
    'in-progress', 'busy', 'no-answer', 'completed', 'failed', 'canceled'];
function getCallFailureCategory(status) {
    if (status === 400) {
        return 'validation';
    }
    if (status === 401 || status === 403) {
        return 'authorization';
    }
    if (status === 429) {
        return 'rate_limited';
    }
    if (status >= 500) {
        return 'service';
    }
    return status === 0 ? 'network' : 'request';
}
function getVRSConfig() {
    const defaults = {
        twilioVoiceUrl: 'http://localhost:3002',
        // Self-hosted Voice SDK 2.x (static/, served by vrs-server): the prod
        // CSP is script-src 'self', and Twilio's gateway rejects the EOL 1.x
        // client with "Client version not supported".
        twilioClientSdkUrl: '/static/twilio-voice-sdk-2.18.3.min.js',
        opsApiUrl: 'http://localhost:3003/api',
        callLogging: {
            enabled: true,
            logEvents: true,
            logQuality: true
        }
    };
    if (typeof config !== 'undefined' && config.vrs) {
        return { ...defaults, ...config.vrs };
    }
    return defaults;
}
class TwilioVoiceService {
    constructor() {
        this.listeners = new Map();
        this.currentCall = { status: 'idle' };
        this.pendingDeviceContext = null;
        this.currentDeviceContext = null;
        this.twilioDevice = null;
        this.isTwilioDeviceReady = false;
        this.currentInterpreterIdentity = null;
        this.currentConnection = null;
        this.pendingIncomingConnection = null;
        this.currentVcoGrant = null;
        this.currentVcoRoomName = null;
        this.vcoStatus = { muted: false, speaking: false, controlPending: false,
            status: 'idle', roomName: null, role: null };
        this.vcoAccount = null;
        this.vcoRevision = 0;
        this.vcoControlUnconfirmed = false;
        this.vcoControl = null;
        this.disconnectingAudio = new WeakMap();
        this.activePlatformCallId = null;
        this.preserveActiveCallOnNextCleanup = false;
        this.hangupInFlight = null;
        this.endingPhoneLegs = new Map();
        this.pollingEpoch = 0;
        this.statusPollAbort = null;
        this.phoneProviderStatus = null;
        this.phoneAudioState = 'connecting';
        this.phoneUsesVco = false;
        this.phoneConnectedAt = null;
        this.phoneStatusUnconfirmed = false;
        this.incomingAudio = new Map();
        this.lifecycleEpoch = 0;
        this.phoneStartInFlight = null;
        this.vcoStartInFlight = null;
        this.phoneCreateAbort = null;
        this.phoneAttempt = null;
        this.confirmedPhoneAttemptId = null;
        // The live conference the primary PSTN leg joined; additional hearing
        // parties (Add Call) dial into the same one.
        this.activeConference = null;
        this.addedLegSids = [];
        this.config = getVRSConfig();
    }
    getInterpreterIdentity(explicitIdentity) {
        if (explicitIdentity) {
            return explicitIdentity;
        }
        const storedUser = getPersistentJson('vrs_user_info');
        const storedToken = getPersistentJson('vrs_auth_token');
        const storedRole = getPersistentItem('vrs_user_role');
        return storedUser?.id
            || storedToken?.userId
            || (storedRole === 'interpreter'
                ? `interpreter-${Date.now()}`
                : `interpreter-${Date.now()}`);
    }
    async loadTwilioSdk() {
        if (typeof window === 'undefined') {
            throw new Error('Twilio browser client can only be initialized in the browser.');
        }
        if (window.Twilio?.Device) {
            return window.Twilio;
        }
        let loadingScript;
        const loading = new Promise((resolve, reject) => {
            const existingScript = document.querySelector('script[data-twilio-client-sdk="true"]');
            if (existingScript) {
                loadingScript = existingScript;
                const loadableScript = existingScript;
                loadableScript.onload = () => resolve();
                loadableScript.onerror = () => reject(new Error('Failed to load Twilio Voice SDK.'));
                return;
            }
            const script = document.createElement('script');
            loadingScript = script;
            script.src = this.config.twilioClientSdkUrl;
            script.async = true;
            script.dataset.twilioClientSdk = 'true';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Twilio Voice SDK.'));
            document.head.appendChild(script);
        });
        try {
            await timeoutPromise(loading, 10000);
            if (!window.Twilio?.Device)
                throw new Error('Twilio Voice SDK loaded, but Twilio.Device is unavailable.');
        }
        catch (error) {
            loadingScript?.remove?.();
            throw error;
        }
        return window.Twilio;
    }
    async fetchClientToken(platformCallId) {
        const result = unwrapApiResponse(await apiClient.post('/api/interpreter/voice/token', platformCallId ? { platformCallId } : {}));
        if (!result.token || !result.identity) {
            throw new Error('Failed to fetch Twilio client token.');
        }
        return { identity: result.identity, token: result.token };
    }
    voiceAccountKey() {
        const role = getPersistentItem('vrs_user_role');
        const profile = getPersistentJson('vrs_user_info');
        return (role === 'client' || role === 'interpreter') && typeof profile?.id === 'string' && profile.id
            ? JSON.stringify([role, profile.id]) : null;
    }
    async ensureDeviceReady(identity, tokenOverride, platformCallId) {
        const fallbackIdentity = this.getInterpreterIdentity(identity);
        const account = this.voiceAccountKey();
        if (!account)
            throw new Error('Your signed-in profile is required to connect phone audio.');
        const context = JSON.stringify([account, tokenOverride ? 'vco' : 'regular', tokenOverride ? fallbackIdentity : '']);
        if (this.twilioDevice && this.currentInterpreterIdentity && this.isTwilioDeviceReady && this.currentDeviceContext === context) {
            if (tokenOverride)
                this.twilioDevice.updateToken?.(tokenOverride);
            return this.currentInterpreterIdentity;
        }
        if (this.deviceReadyPromise) {
            if (this.pendingDeviceContext !== context)
                throw new Error('Another phone authorization is still starting. Try again.');
            await this.deviceReadyPromise;
            return this.currentInterpreterIdentity || fallbackIdentity;
        }
        if (this.currentConnection)
            throw new Error('End the existing phone audio before changing its authorization.');
        const operation = (async () => {
            const Twilio = await this.loadTwilioSdk();
            if (this.voiceAccountKey() !== account)
                throw new Error('Your account changed during phone authorization.');
            const voiceToken = tokenOverride
                ? { identity: fallbackIdentity, token: tokenOverride }
                : await this.fetchClientToken(platformCallId);
            if (this.voiceAccountKey() !== account)
                throw new Error('Your account changed during phone authorization.');
            const { identity: interpreterIdentity, token } = voiceToken;
            this.currentInterpreterIdentity = interpreterIdentity || fallbackIdentity;
            this.twilioDevice?.destroy?.();
            const device = new Twilio.Device(token, { closeProtection: true });
            this.twilioDevice = device;
            this.currentDeviceContext = context;
            this.isTwilioDeviceReady = false;
            const ownsDevice = () => this.twilioDevice === device && this.currentDeviceContext === context && this.voiceAccountKey() === account;
            const reportDeviceFailure = (message) => {
                if (!ownsDevice() || this.currentCall.hangupPending)
                    return;
                this.isTwilioDeviceReady = false;
                if (this.currentVcoGrant) {
                    this.updateVcoStatus({ status: 'failed', error: message });
                }
                else {
                    this.updateCallStatus({ status: 'failed', error: message,
                        hangupPending: Boolean(this.phoneAttempt || this.currentConnection || this.currentCall.callSid) });
                }
                this.emit('deviceError', { message });
                void reportMobileError(new Error(message), { event: 'vrs_voice_device_failed' })
                    .catch(() => { });
            };
            device.on('error', () => reportDeviceFailure('Phone audio connection failed. End the phone leg and try again.'));
            device.on('incoming', (value) => {
                const connection = value;
                this.incomingAudio.set(connection, { account, epoch: this.lifecycleEpoch });
                if (!ownsDevice() || this.currentCall.hangupPending || this.currentConnection || this.pendingIncomingConnection
                    || !this.phoneAttempt && !this.currentCall.callSid) {
                    void this.disconnectBrowserAudio(connection).then(() => this.incomingAudio.delete(connection)).catch(() => {
                        emitMobileTelemetryEvent('vrs_pstn_incoming_audio_close_failed', {}, { state: 'failed' });
                        this.updateCallStatus({ status: 'failed', hangupPending: true,
                            error: 'Incoming phone audio could not be closed. Retry ending the phone leg.' });
                    });
                    return;
                }
                this.pendingIncomingConnection = connection;
                this.updateCallStatus({ status: 'ringing' });
                this.emit('incomingCall', { status: 'ringing' });
            });
            if (!tokenOverride) {
                device.on('tokenWillExpire', () => {
                    if (!ownsDevice() || this.currentCall.hangupPending)
                        return;
                    this.fetchClientToken(this.activePlatformCallId || undefined).then(fresh => {
                        if (!ownsDevice() || this.currentCall.hangupPending)
                            return;
                        if (fresh.identity !== interpreterIdentity)
                            throw new Error('Phone authorization identity changed.');
                        device.updateToken?.(fresh.token);
                    }).catch(() => reportDeviceFailure('Phone authorization could not be refreshed. End the phone leg and try again.'));
                });
            }
            await timeoutPromise(device.register(), 10000);
            if (!ownsDevice())
                throw new Error('Your account changed during phone authorization.');
            this.isTwilioDeviceReady = true;
            this.emit('deviceReady', { identity: this.currentInterpreterIdentity });
        })();
        this.deviceReadyPromise = operation;
        this.pendingDeviceContext = context;
        try {
            await operation;
            return this.currentInterpreterIdentity || fallbackIdentity;
        }
        catch (error) {
            if (this.currentDeviceContext === context)
                this.isTwilioDeviceReady = false;
            throw error;
        }
        finally {
            if (this.deviceReadyPromise === operation) {
                this.deviceReadyPromise = undefined;
                this.pendingDeviceContext = null;
            }
        }
    }
    canAddParticipant() {
        return Boolean(this.activeConference) && !this.currentCall.hangupPending;
    }
    addedParticipantCount() {
        return this.addedLegSids.length;
    }
    /** Patch a third/fourth hearing party into the live conference. */
    async addParticipant(phoneNumber) {
        if (!this.activeConference || this.currentCall.hangupPending) {
            return false;
        }
        const result = await apiClient.post('/api/interpreter/voice/call', {
            addLeg: true,
            conferenceName: this.activeConference.conferenceName,
            phoneNumber,
            platformCallId: this.activePlatformCallId || undefined,
            sessionId: this.activeConference.sessionId
        });
        if (!result.error && result.data?.callSid) {
            this.addedLegSids.push(result.data.callSid);
            return true;
        }
        return false;
    }
    /** Drop the most recently added hearing party, keeping the call alive. */
    async dropLastAddedParticipant() {
        if (this.currentCall.hangupPending)
            return false;
        const sid = this.addedLegSids[this.addedLegSids.length - 1];
        if (!sid)
            return false;
        try {
            await this.endPhoneLeg(sid, this.activePlatformCallId);
            this.addedLegSids = this.addedLegSids.filter(value => value !== sid);
            return true;
        }
        catch {
            return false;
        }
    }
    async makeCall(options) {
        if (this.phoneStartInFlight || this.currentCall.hangupPending)
            return false;
        if (this.phoneAttempt || this.currentCall.callSid || this.incomingAudio.size || this.currentConnection && !this.currentVcoGrant) {
            if (['ended', 'failed', 'idle'].includes(this.currentCall.status)) {
                this.updateCallStatus({ hangupPending: true, error: 'End the previous phone attempt before starting another call.' });
            }
            return false;
        }
        const epoch = this.lifecycleEpoch;
        const operation = Promise.resolve().then(() => this.startPhoneCall(options, epoch));
        this.phoneStartInFlight = operation;
        this.phoneProviderStatus = null;
        this.phoneAudioState = 'connecting';
        this.phoneUsesVco = options.vcoEnabled === true;
        this.phoneConnectedAt = null;
        this.phoneStatusUnconfirmed = false;
        this.updateCallStatus({ status: 'dialing', startTime: undefined, endTime: undefined, duration: undefined,
            connectionMessage: 'Starting phone audio…', error: undefined });
        try {
            return await operation;
        }
        finally {
            if (this.phoneStartInFlight === operation)
                this.phoneStartInFlight = null;
        }
    }
    async startPhoneCall(options, epoch) {
        const cancelled = () => epoch !== this.lifecycleEpoch || Boolean(this.currentCall.hangupPending);
        if (cancelled())
            return false;
        try {
            const actorId = getBrowserVoiceActorId();
            if (!actorId)
                throw new Error('Your signed-in interpreter profile is required to start phone audio.');
            const previous = await loadBrowserVoiceAttempt();
            if (previous) {
                this.phoneAttempt = previous;
                if (!cancelled())
                    this.updateCallStatus({ status: 'failed', hangupPending: true,
                        error: 'End the previous phone attempt before starting another call.' });
                return false;
            }
            if (cancelled())
                return false;
            if (!options.platformCallId)
                throw new Error('The current platform call is required to start phone audio.');
            if (options.vcoEnabled) {
                await this.joinVcoBridge({ role: 'interpreter', roomName: options.sessionId });
            }
            else {
                await this.ensureDeviceReady(options.interpreterId, undefined, options.platformCallId);
            }
            if (cancelled())
                return false;
            const reservation = await reserveBrowserVoiceAttempt(options.platformCallId, options.sessionId, actorId);
            this.phoneAttempt = reservation.attempt;
            this.activePlatformCallId = options.platformCallId;
            if (!reservation.created) {
                if (!cancelled())
                    this.updateCallStatus({ status: 'failed', hangupPending: true,
                        error: 'End the previous phone attempt before starting another call.' });
                return false;
            }
            if (cancelled())
                return false;
            const attempt = reservation.attempt;
            const controller = new AbortController();
            this.phoneCreateAbort = controller;
            const result = await apiClient.post('/api/interpreter/voice/call', {
                attemptId: attempt.attemptId,
                answerHeldInbound: options.answerHeldInbound === true,
                phoneNumber: options.phoneNumber, platformCallId: options.platformCallId, sessionId: options.sessionId
            }, { signal: controller.signal }).finally(() => {
                if (this.phoneCreateAbort === controller)
                    this.phoneCreateAbort = null;
            });
            if (this.confirmedPhoneAttemptId === attempt.attemptId)
                return false;
            if (getBrowserVoiceActorId() !== actorId)
                throw new Error('Your interpreter account changed during phone startup. Recover the original attempt before dialing.');
            if (!result.error && typeof result.data?.callSid === 'string' && result.data.callSid) {
                // Retain the original remote target even if local startup or further validation fails.
                this.currentCall = { ...this.currentCall, callSid: result.data.callSid };
                if (cancelled())
                    return false;
                if (!await ownsBrowserVoiceAttempt(attempt))
                    throw new Error('The original phone attempt changed in another tab. Recover it before dialing.');
                if (cancelled())
                    return false;
                if (options.answerHeldInbound && result.data.inboundAnswer !== true) {
                    throw new Error('The hearing caller is no longer waiting on the line.');
                }
                const conferenceName = result.data.conferenceName;
                this.activePlatformCallId = options.platformCallId || null;
                if (conferenceName) {
                    this.activeConference = {
                        conferenceName,
                        sessionId: result.data.sessionId || options.sessionId
                    };
                    this.addedLegSids = [];
                }
                if (phoneProviderStatuses.includes(result.data.status || '')) {
                    // Answering a held inbound call can acknowledge in-progress before the
                    // conference participant joins. Only the status endpoint confirms readiness.
                    this.phoneProviderStatus = result.data.status === 'in-progress' ? 'answering' : result.data.status;
                }
                this.refreshPhoneReadiness();
                if (cancelled())
                    return false;
                this.startStatusPolling(result.data.callSid, options.platformCallId);
                if (conferenceName && this.twilioDevice?.connect && !this.currentConnection) {
                    this.currentConnection = await this.twilioDevice.connect({
                        params: {
                            bridgeMode: 'conference',
                            conferenceName,
                            sessionId: result.data.sessionId || options.sessionId
                        }
                    });
                    if (cancelled())
                        return false;
                    if (getBrowserVoiceActorId() !== actorId || !await ownsBrowserVoiceAttempt(attempt)) {
                        throw new Error('The original phone attempt changed during audio startup. Recover it before dialing.');
                    }
                    if (cancelled())
                        return false;
                    this.watchPhoneConnection(this.currentConnection);
                }
                this.refreshPhoneReadiness();
                // Successful dials were invisible in Client Telemetry Events —
                // only failures reported, so "did the interpreter dial?" was
                // unanswerable from the admin panels. Last four digits only,
                // under a key the redaction filter does not (and should not)
                // scrub, since four digits identify no one.
                emitMobileTelemetryEvent('vrs_pstn_call_started', {
                    dialedLast4: String(options.phoneNumber || '').replace(/\D/g, '').slice(-4),
                    inboundAnswer: Boolean(result.data.inboundAnswer)
                }, {
                    roomName: options.sessionId,
                    state: 'dialing'
                });
                return true;
            }
            if (cancelled())
                return false;
            this.updateCallStatus({
                status: 'failed', hangupPending: Boolean(this.phoneAttempt),
                error: result.error || 'The original phone attempt could not be confirmed. Retry End before dialing.'
            });
            emitMobileTelemetryEvent('vrs_pstn_call_failed', {
                category: getCallFailureCategory(result.status),
                httpStatus: result.status
            }, {
                roomName: options.sessionId,
                state: 'failed'
            });
            // A failed dial-out is an operator-visible failure, not just a
            // red toast for the interpreter: it must land in Client Error
            // Reports with enough context to diagnose without a live repro.
            // The 2026-07-29 dial-out outage produced no error report at all
            // — this call is what closes that gap. Last four digits only;
            // full numbers stay out of stored reports.
            reportMobileError(new Error(result.error || 'Call failed'), {
                event: 'vrs_pstn_call_failed',
                httpStatus: result.status,
                phoneLast4: String(options.phoneNumber || '').replace(/\D/g, '').slice(-4),
                sessionId: options.sessionId
            }).catch(() => { });
            return false;
        }
        catch (error) {
            if (cancelled())
                return false;
            console.error('Error making call:', error);
            this.updateCallStatus({
                status: 'failed', hangupPending: Boolean(this.phoneAttempt),
                error: error instanceof Error ? error.message : 'Network error'
            });
            emitMobileTelemetryEvent('vrs_pstn_call_failed', {
                category: 'client',
                errorName: error instanceof Error ? error.name : 'UnknownError'
            }, {
                roomName: options.sessionId,
                state: 'failed'
            });
            reportMobileError(error, {
                event: 'vrs_pstn_call_failed',
                phoneLast4: String(options.phoneNumber || '').replace(/\D/g, '').slice(-4),
                sessionId: options.sessionId
            }).catch(() => { });
            return false;
        }
    }
    async joinVcoBridge(options) {
        const { role, roomName } = options;
        if (this.currentCall.hangupPending)
            throw new Error('Finish ending the previous phone audio before reconnecting.');
        if (this.vcoStartInFlight) {
            if (this.vcoStartInFlight.role === role && this.vcoStartInFlight.roomName === roomName)
                return this.vcoStartInFlight.promise;
            throw new Error('Another VCO audio connection is still starting.');
        }
        const previous = this.currentConnection;
        if (previous) {
            if (this.currentVcoGrant?.role !== `vco_${role}` || this.currentVcoRoomName !== roomName) {
                throw new Error('End the existing phone audio before connecting to another room.');
            }
            if (this.vcoStatus.status !== 'failed')
                return;
        }
        const epoch = this.lifecycleEpoch;
        const revision = ++this.vcoRevision;
        const promise = Promise.resolve().then(async () => {
            if (previous) {
                try {
                    await this.disconnectBrowserAudio(previous);
                }
                catch {
                    throw new Error('VCO audio could not be disconnected. Retry reconnecting voice or end the call.');
                }
                if (epoch !== this.lifecycleEpoch || this.currentCall.hangupPending)
                    return;
                if (this.currentConnection === previous) {
                    this.currentConnection = null;
                    this.currentVcoGrant = null;
                    this.currentVcoRoomName = null;
                }
            }
            await this.startVcoBridge(options, epoch);
        });
        const operation = { ...options, promise };
        this.vcoStartInFlight = operation;
        this.updateVcoStatus({ muted: previous ? this.vcoStatus.muted : false,
            speaking: previous ? this.vcoStatus.speaking : false, controlPending: false, error: undefined,
            role, roomName, status: 'connecting' });
        try {
            await promise;
        }
        catch (error) {
            if (epoch === this.lifecycleEpoch && revision === this.vcoRevision && !this.currentCall.hangupPending) {
                this.updateVcoStatus({ status: 'failed', error: 'VCO audio could not connect. Reconnect voice or end the call.' });
            }
            throw error;
        }
        finally {
            if (this.vcoStartInFlight === operation)
                this.vcoStartInFlight = null;
        }
    }
    async startVcoBridge({ role, roomName }, epoch) {
        const cancelled = () => epoch !== this.lifecycleEpoch || Boolean(this.currentCall.hangupPending);
        if (cancelled())
            return;
        const account = this.voiceAccountKey();
        if (!account)
            throw new Error('Your signed-in profile is required to connect phone audio.');
        const endpoint = role === 'interpreter'
            ? '/api/interpreter/voice/vco-grant'
            : '/api/client/voice/vco-grant';
        const result = unwrapApiResponse(await apiClient.post(endpoint, { roomName }));
        if (cancelled())
            return;
        if (this.voiceAccountKey() !== account)
            throw new Error('Your account changed during VCO authorization.');
        if (!result.bridgeToken || !result.conferenceName || !result.identity || result.role !== `vco_${role}` || !result.token) {
            throw new Error('VCO audio authorization was incomplete.');
        }
        await this.ensureDeviceReady(result.identity, result.token);
        if (cancelled())
            return;
        if (!this.twilioDevice?.connect) {
            throw new Error('Twilio audio bridge is unavailable.');
        }
        this.currentVcoGrant = {
            bridgeToken: result.bridgeToken,
            conferenceName: result.conferenceName,
            identity: result.identity,
            role: result.role
        };
        this.currentVcoRoomName = roomName;
        this.vcoAccount = account;
        this.vcoControlUnconfirmed = false;
        this.currentConnection = await this.twilioDevice.connect({
            params: {
                bridgeMode: 'conference',
                bridgeRole: result.role,
                bridgeToken: result.bridgeToken,
                conferenceName: result.conferenceName,
                sessionId: roomName
            }
        });
        if (cancelled())
            return;
        if (this.voiceAccountKey() !== account)
            throw new Error('Your account changed during VCO audio startup. End the phone audio.');
        this.watchVcoConnection(this.currentConnection);
    }
    getVcoStatus(options) {
        if (options && (this.vcoStatus.roomName !== options.roomName || this.vcoStatus.role !== options.role)) {
            return { status: 'idle', muted: false, speaking: false, controlPending: false, ...options };
        }
        return { ...this.vcoStatus };
    }
    updateVcoStatus(update) {
        this.vcoStatus = { ...this.vcoStatus, ...update };
        this.emit('vcoStatusChange', { ...this.vcoStatus });
        if (update.speaking !== undefined)
            this.emit('vcoInterpreterSpeakingChange', update.speaking);
        if (this.phoneUsesVco && this.currentCall.callSid)
            this.refreshPhoneReadiness();
    }
    watchVcoConnection(call) {
        const grant = this.currentVcoGrant;
        const revision = this.vcoRevision;
        const owns = () => this.currentConnection === call && this.currentVcoGrant === grant
            && revision === this.vcoRevision && !this.currentCall.hangupPending
            && !this.disconnectingAudio.has(call) && this.voiceAccountKey() === this.vcoAccount;
        const refreshMute = () => {
            const muted = call.isMuted?.();
            if (typeof muted !== 'boolean')
                throw new Error('Voice mute state is unavailable.');
            this.updateVcoStatus({ muted });
        };
        const failed = () => {
            if (owns())
                this.updateVcoStatus({ status: 'failed', controlPending: false,
                    error: 'VCO audio is unavailable. Reconnect voice or end the call.' });
        };
        const connected = () => {
            if (!owns())
                return;
            try {
                refreshMute();
                if (!this.vcoControlUnconfirmed)
                    this.updateVcoStatus({ status: 'connected', error: undefined });
            }
            catch {
                failed();
            }
        };
        call.on?.('accept', connected);
        call.on?.('reconnected', connected);
        call.on?.('reconnecting', () => {
            if (owns() && !this.vcoControlUnconfirmed)
                this.updateVcoStatus({ status: 'connecting' });
        });
        call.on?.('mute', () => {
            if (!owns())
                return;
            try {
                refreshMute();
            }
            catch {
                failed();
            }
        });
        call.on?.('error', failed);
        const disconnected = () => {
            if (!owns())
                return;
            this.updateVcoStatus({ status: 'failed', speaking: false, controlPending: false,
                error: 'VCO audio disconnected. Reconnect voice or end the call.' });
        };
        call.on?.('disconnect', disconnected);
        call.on?.('cancel', disconnected);
        if (call.status?.() === 'open')
            connected();
        else if (call.status?.() === 'closed')
            disconnected();
    }
    async runVcoControl(call, change) {
        if (this.currentConnection !== call || this.currentCall.hangupPending || this.vcoStatus.status !== 'connected'
            || this.voiceAccountKey() !== this.vcoAccount)
            throw new Error('VCO audio is not connected for this account.');
        if (this.vcoControl?.call === call)
            throw new Error('An audio change is still pending. Wait for confirmation or end the call.');
        const revision = this.vcoRevision;
        let active = true;
        const controller = new AbortController();
        const owns = () => active && revision === this.vcoRevision && this.currentConnection === call
            && !this.currentCall.hangupPending && this.voiceAccountKey() === this.vcoAccount;
        const promise = Promise.resolve().then(() => { if (owns())
            return change(owns, controller.signal); });
        const operation = { call, promise };
        this.vcoControl = operation;
        this.updateVcoStatus({ controlPending: true, error: undefined });
        try {
            await timeoutPromise(promise, 15000);
        }
        catch {
            const message = 'The audio change could not be confirmed. Reconnect voice or end the call.';
            if (owns()) {
                this.vcoControlUnconfirmed = true;
                this.updateVcoStatus({ status: 'failed', error: message });
            }
            throw new Error(message);
        }
        finally {
            const stillOwned = owns();
            active = false;
            controller.abort();
            if (this.vcoControl === operation)
                this.vcoControl = null;
            if (stillOwned)
                this.updateVcoStatus({ controlPending: false });
        }
    }
    async setVcoMuted(muted) {
        const call = this.currentConnection;
        if (!call || !this.currentVcoGrant)
            throw new Error('VCO audio is not connected.');
        await this.runVcoControl(call, async (owns) => {
            if (!call.mute)
                throw new Error('Voice mute is unavailable.');
            call.mute(muted);
            const confirmed = call.isMuted?.();
            if (confirmed !== muted)
                throw new Error('Voice mute could not be confirmed.');
            if (owns())
                this.updateVcoStatus({ muted: confirmed, error: undefined });
        });
    }
    sendDigits(digits) {
        if (/^[0-9*#]+$/.test(digits)) {
            this.currentConnection?.sendDigits?.(digits);
        }
    }
    async setInterpreterSpeaking(speaking) {
        const call = this.currentConnection;
        const grant = this.currentVcoGrant;
        const roomName = this.currentVcoRoomName;
        const participantCallSid = call?.parameters?.CallSid;
        if (!call || !grant || grant.role !== 'vco_interpreter' || !roomName
            || typeof participantCallSid !== 'string' || !/^CA[0-9a-fA-F]{32}$/.test(participantCallSid)) {
            throw new Error('Interpreter VCO audio is not connected to a confirmed participant.');
        }
        await this.runVcoControl(call, async (owns, signal) => {
            const response = await apiClient.post('/api/interpreter/voice/vco-speaking', {
                bridgeToken: grant.bridgeToken, participantCallSid, roomName, speaking
            }, { signal });
            const confirmed = vcoSpeakingResponseSchema.safeParse(response.data);
            if (response.error || !confirmed.success || confirmed.data.speaking !== speaking || confirmed.data.muted !== !speaking
                || confirmed.data.conferenceName !== grant.conferenceName || confirmed.data.identity !== grant.identity
                || confirmed.data.participantCallSid !== participantCallSid) {
                throw new Error('Interpreter audio could not be confirmed.');
            }
            if (owns() && this.currentVcoGrant === grant)
                this.updateVcoStatus({ speaking, error: undefined });
        });
    }
    clearVcoState() {
        this.vcoRevision++;
        this.currentVcoGrant = null;
        this.currentVcoRoomName = null;
        this.vcoAccount = null;
        this.vcoControlUnconfirmed = false;
        this.updateVcoStatus({ muted: false, speaking: false, controlPending: false, error: undefined, status: 'idle' });
    }
    watchPhoneConnection(connection) {
        const epoch = this.lifecycleEpoch;
        const account = this.voiceAccountKey();
        const owns = () => this.currentConnection === connection && epoch === this.lifecycleEpoch
            && account === this.voiceAccountKey() && !this.currentCall.hangupPending;
        const updateAudio = (state) => {
            if (!owns())
                return;
            this.phoneAudioState = state;
            this.refreshPhoneReadiness();
        };
        connection.on?.('accept', () => updateAudio('open'));
        // The SDK emits reconnected before its status() becomes open.
        connection.on?.('reconnected', () => updateAudio('open'));
        connection.on?.('reconnecting', () => updateAudio('reconnecting'));
        for (const event of ['disconnect', 'cancel', 'reject', 'error']) {
            connection.on?.(event, () => updateAudio('failed'));
        }
        const state = connection.status?.();
        updateAudio(state === 'closed' ? 'failed' : state === 'open' ? 'open' : 'connecting');
    }
    acceptIncomingCall() {
        const connection = this.pendingIncomingConnection;
        const owner = connection && this.incomingAudio.get(connection);
        if (!connection || !owner || owner.epoch !== this.lifecycleEpoch || owner.account !== this.voiceAccountKey()
            || this.currentCall.hangupPending)
            return false;
        this.currentConnection = connection;
        this.pendingIncomingConnection = null;
        this.watchPhoneConnection(connection);
        try {
            connection.accept();
            return true;
        }
        catch {
            this.phoneAudioState = 'failed';
            this.refreshPhoneReadiness();
            return false;
        }
    }
    rejectIncomingCall() {
        if (this.pendingIncomingConnection)
            void this.hangupCall().catch(() => undefined);
    }
    async hangupCall() {
        if (this.hangupInFlight)
            return this.hangupInFlight;
        this.lifecycleEpoch++;
        this.stopDurationTimer();
        this.stopStatusPolling();
        this.updateCallStatus({ hangupPending: true, error: undefined });
        this.phoneCreateAbort?.abort();
        const operation = Promise.resolve().then(() => this.finishHangup());
        this.hangupInFlight = operation;
        try {
            await operation;
        }
        finally {
            if (this.hangupInFlight === operation)
                this.hangupInFlight = null;
        }
    }
    async endPhoneLeg(callSid, platformCallId) {
        const existing = this.endingPhoneLegs.get(callSid);
        if (existing)
            return existing;
        const controller = new AbortController();
        const operation = timeoutPromise(Promise.resolve().then(async () => {
            const response = await apiClient.post('/api/interpreter/voice/hangup', { callSid, platformCallId: platformCallId || undefined }, { signal: controller.signal });
            if (response.error || response.data?.success !== true || response.data.twilio?.success !== true) {
                throw new Error('Phone termination could not be confirmed.');
            }
        }), 15000);
        this.endingPhoneLegs.set(callSid, operation);
        try {
            await operation;
        }
        finally {
            controller.abort();
            if (this.endingPhoneLegs.get(callSid) === operation)
                this.endingPhoneLegs.delete(callSid);
        }
    }
    async disconnectBrowserAudio(connection) {
        const existing = this.disconnectingAudio.get(connection);
        if (existing)
            return existing;
        const operation = Promise.resolve().then(() => this.confirmBrowserAudioDisconnected(connection));
        this.disconnectingAudio.set(connection, operation);
        try {
            await operation;
        }
        finally {
            if (this.disconnectingAudio.get(connection) === operation)
                this.disconnectingAudio.delete(connection);
        }
    }
    async confirmBrowserAudioDisconnected(connection) {
        if (connection.status?.() === 'closed')
            return;
        let onDisconnected = () => { };
        const completed = new Promise((resolve, reject) => {
            onDisconnected = () => resolve();
            for (const event of ['disconnect', 'cancel', 'reject'])
                connection.on?.(event, onDisconnected);
            try {
                const rejectPending = connection.status?.() === 'pending'
                    || this.incomingAudio.has(connection) && this.currentConnection !== connection;
                const close = rejectPending ? connection.reject : connection.disconnect;
                if (!close)
                    throw new Error('Browser audio disconnect is unavailable.');
                close.call(connection);
                if (connection.status?.() === 'closed')
                    resolve();
            }
            catch (error) {
                reject(error);
            }
        });
        try {
            await timeoutPromise(completed, 15000);
        }
        finally {
            for (const event of ['disconnect', 'cancel', 'reject'])
                connection.removeListener?.(event, onDisconnected);
        }
    }
    async finishHangup() {
        const pendingStarts = [this.phoneStartInFlight, this.vcoStartInFlight?.promise].filter(Boolean);
        const attemptedConnections = new Set();
        const attemptedTargets = new Set();
        const known = (!this.phoneAttempt && !this.currentCall.callSid
            ? loadBrowserVoiceAttempt().then(attempt => { this.phoneAttempt = attempt; }) : Promise.resolve())
            .then(() => this.cleanupCallTargets(attemptedConnections, attemptedTargets))
            .catch(() => ({ localFailed: false, remoteFailed: true }));
        const pending = pendingStarts.length
            ? timeoutPromise(Promise.allSettled(pendingStarts).then(() => this.cleanupCallTargets(attemptedConnections, attemptedTargets)), 15000)
                .catch(() => ({ localFailed: false, remoteFailed: false, startupUnconfirmed: true }))
            : Promise.resolve({ localFailed: false, remoteFailed: false, startupUnconfirmed: false });
        const results = await Promise.all([known, pending]);
        const localDisconnectFailed = results.some(result => result.localFailed) || this.incomingAudio.size > 0;
        const remoteHangupFailed = results.some(result => result.remoteFailed);
        const startupUnconfirmed = results.some(result => 'startupUnconfirmed' in result && result.startupUnconfirmed);
        if (localDisconnectFailed || remoteHangupFailed || startupUnconfirmed) {
            const error = 'Phone hangup could not be confirmed. Retry ending the phone leg.';
            this.updateCallStatus({ status: 'failed', hangupPending: true, error });
            emitMobileTelemetryEvent('vrs_pstn_hangup_failed', { localDisconnectFailed, remoteHangupFailed, startupUnconfirmed }, { callId: this.activePlatformCallId || undefined, state: 'failed' });
            throw new Error(error);
        }
        this.activeConference = null;
        this.activePlatformCallId = null;
        this.updateCallStatus({ callSid: undefined, hangupPending: false, error: undefined, connectionMessage: undefined,
            status: 'ended', endTime: new Date() });
    }
    async cleanupCallTargets(attemptedConnections, attemptedTargets) {
        const connection = this.currentConnection;
        const primarySid = this.currentCall.callSid;
        const attempt = this.phoneAttempt;
        const platformCallId = this.activePlatformCallId;
        const targets = [...new Set([...(attempt ? [] : [primarySid]), ...this.addedLegSids]
                .filter((sid) => Boolean(sid)))];
        const results = await Promise.allSettled([
            (async () => {
                const connections = new Set([...this.incomingAudio.keys(), ...(connection ? [connection] : [])]);
                const local = await Promise.allSettled([...connections].map(async (target) => {
                    if (attemptedConnections.has(target))
                        return;
                    attemptedConnections.add(target);
                    await this.disconnectBrowserAudio(target);
                    this.incomingAudio.delete(target);
                    if (this.pendingIncomingConnection === target)
                        this.pendingIncomingConnection = null;
                    if (this.currentConnection === target) {
                        this.currentConnection = null;
                        this.clearVcoState();
                    }
                }));
                if (local.some(result => result.status === 'rejected'))
                    throw new Error('Browser audio remains unconfirmed.');
                if (!this.currentConnection)
                    this.clearVcoState();
            })(),
            (async () => {
                if (!attempt || attemptedTargets.has(`attempt:${attempt.attemptId}`))
                    return;
                attemptedTargets.add(`attempt:${attempt.attemptId}`);
                if (getBrowserVoiceActorId() !== attempt.actorId)
                    throw new Error('The original interpreter account is required.');
                const controller = new AbortController();
                try {
                    await timeoutPromise(apiClient.post('/api/interpreter/voice/attempts/cancel', {
                        attemptId: attempt.attemptId, platformCallId: attempt.platformCallId, sessionId: attempt.sessionId
                    }, { signal: controller.signal }).then(async (response) => {
                        if (response.error || !voiceAttemptCancellationResponseSchema.safeParse(response.data).success) {
                            throw new Error('Original phone termination could not be confirmed.');
                        }
                        this.confirmedPhoneAttemptId = attempt.attemptId;
                        await clearBrowserVoiceAttempt(attempt);
                        if (this.phoneAttempt === attempt) {
                            this.phoneAttempt = null;
                            this.updateCallStatus({ callSid: undefined });
                        }
                    }), 15000);
                }
                finally {
                    controller.abort();
                }
            })(),
            ...targets.map(async (sid) => {
                if (attemptedTargets.has(sid))
                    return;
                attemptedTargets.add(sid);
                await this.endPhoneLeg(sid, platformCallId);
                this.addedLegSids = this.addedLegSids.filter(value => value !== sid);
                if (this.currentCall.callSid === sid)
                    this.updateCallStatus({ callSid: undefined });
            })
        ]);
        return { localFailed: results[0].status === 'rejected', remoteFailed: results.slice(1).some(result => result.status === 'rejected') };
    }
    getCallStatus() {
        return { ...this.currentCall };
    }
    getCallDuration() {
        if (!this.currentCall.startTime || this.currentCall.status !== 'connected') {
            return '';
        }
        const now = new Date();
        const duration = Math.floor((now.getTime() - this.currentCall.startTime.getTime()) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    startStatusPolling(callSid, platformCallId) {
        this.stopStatusPolling();
        const epoch = this.pollingEpoch;
        const account = this.voiceAccountKey();
        let requestPending = false;
        const owns = () => epoch === this.pollingEpoch && this.currentCall.callSid === callSid
            && !this.currentCall.hangupPending && account === this.voiceAccountKey();
        const poll = async () => {
            if (!owns() || requestPending)
                return;
            requestPending = true;
            const controller = new AbortController();
            this.statusPollAbort = controller;
            try {
                const response = await timeoutPromise(apiClient.get(`/api/interpreter/voice/status/${callSid}${platformCallId
                    ? `?platformCallId=${encodeURIComponent(platformCallId)}` : ''}`, { signal: controller.signal }), 10000);
                if (!owns())
                    return;
                const data = response.data;
                if (response.error || !data || typeof data !== 'object' || !('status' in data)
                    || typeof data.status !== 'string' || !phoneProviderStatuses.includes(data.status)
                    || 'callSid' in data && data.callSid !== callSid) {
                    throw new Error('Phone status is unconfirmed.');
                }
                this.phoneProviderStatus = data.status;
                this.phoneStatusUnconfirmed = false;
                this.refreshPhoneReadiness();
            }
            catch {
                if (!owns())
                    return;
                if (!this.phoneStatusUnconfirmed)
                    emitMobileTelemetryEvent('vrs_pstn_status_unconfirmed', {}, { callId: platformCallId, state: 'unconfirmed' });
                this.phoneStatusUnconfirmed = true;
                this.refreshPhoneReadiness();
            }
            finally {
                controller.abort();
                if (this.statusPollAbort === controller)
                    this.statusPollAbort = null;
                requestPending = false;
            }
        };
        this.statusPollingInterval = setInterval(() => { void poll(); }, 2000);
        void poll();
    }
    stopStatusPolling() {
        this.pollingEpoch++;
        this.statusPollAbort?.abort();
        this.statusPollAbort = null;
        if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = undefined;
        }
    }
    refreshPhoneReadiness() {
        if (!this.currentCall.callSid || this.currentCall.hangupPending)
            return;
        const provider = this.phoneProviderStatus;
        const terminal = {
            busy: 'The phone line is busy.', 'no-answer': 'The phone was not answered.',
            completed: 'The phone participant left.', failed: 'The phone call failed.', canceled: 'The phone call was canceled.'
        };
        if (provider && terminal[provider]) {
            this.stopDurationTimer();
            this.stopStatusPolling();
            this.updateCallStatus({ status: 'failed', hangupPending: true, connectionMessage: undefined,
                error: `${terminal[provider]} End the phone audio before dialing again.` });
            return;
        }
        const audio = this.phoneUsesVco ? this.vcoStatus.status === 'connected' ? 'open' : this.vcoStatus.status
            : this.phoneAudioState;
        if (audio === 'failed' && !this.phoneUsesVco) {
            emitMobileTelemetryEvent('vrs_pstn_audio_failed', {}, { callId: this.activePlatformCallId || undefined, state: 'failed' });
            this.stopDurationTimer();
            this.stopStatusPolling();
            this.updateCallStatus({ status: 'failed', hangupPending: true, connectionMessage: undefined,
                error: 'Phone audio could not stay connected. End the phone leg and try again.' });
            return;
        }
        const connected = provider === 'in-progress' && audio === 'open';
        if (connected && !this.phoneConnectedAt)
            this.phoneConnectedAt = new Date();
        if (connected && !this.durationInterval)
            this.startDurationTimer();
        if (!connected)
            this.stopDurationTimer();
        this.updateCallStatus({ status: connected ? 'connected' : provider === 'ringing' ? 'ringing' : 'dialing',
            startTime: this.phoneConnectedAt || undefined,
            connectionMessage: this.phoneStatusUnconfirmed ? 'Phone status could not be checked. Checking again…'
                : audio === 'reconnecting' ? 'Reconnecting phone audio…'
                    : audio === 'failed' ? 'Phone audio needs recovery. Use Reconnect voice or End.'
                        : provider === 'in-progress' && audio !== 'open' ? 'Phone answered. Connecting your audio…'
                            : !provider ? 'Checking phone status…' : undefined });
    }
    startDurationTimer() {
        if (this.durationInterval) {
            clearInterval(this.durationInterval);
        }
        this.durationInterval = setInterval(() => {
            this.emit('durationUpdate', this.getCallDuration());
        }, 1000);
    }
    stopDurationTimer() {
        if (this.durationInterval) {
            clearInterval(this.durationInterval);
            this.durationInterval = undefined;
        }
    }
    updateCallStatus(update) {
        this.currentCall = { ...this.currentCall, ...update };
        this.emit('statusChange', this.currentCall);
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    off(event, callback) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            const index = eventListeners.indexOf(callback);
            if (index > -1) {
                eventListeners.splice(index, 1);
            }
        }
    }
    emit(event, data) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach(callback => {
                try {
                    callback(data);
                }
                catch (error) {
                    console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }
    cleanup() {
        this.listeners.clear();
        const preservePhoneCall = this.preserveActiveCallOnNextCleanup
            && !this.currentVcoGrant
            && !['ended', 'failed', 'idle'].includes(this.currentCall.status);
        this.preserveActiveCallOnNextCleanup = false;
        if (preservePhoneCall) {
            return;
        }
        this.stopDurationTimer();
        this.stopStatusPolling();
        if (this.incomingAudio.size || this.pendingIncomingConnection || this.currentConnection || this.currentCall.callSid || this.addedLegSids.length || this.currentCall.hangupPending
            || this.phoneAttempt || this.phoneStartInFlight || this.vcoStartInFlight) {
            // The service retains failed targets and reports the failure for explicit retry.
            void this.hangupCall().catch(() => undefined);
        }
    }
    /** Keep the live hearing caller through one intentional room navigation. */
    preserveCurrentCallForNavigation() {
        this.preserveActiveCallOnNextCleanup = true;
    }
    cancelPreserveCurrentCallForNavigation() {
        this.preserveActiveCallOnNextCleanup = false;
    }
}
export const twilioVoiceService = new TwilioVoiceService();
